import type { Workspace } from '@up2stay/up2promo-api-client';
import {
  BillingCycle,
  SubscriptionStatus,
  WorkspacePlan,
} from '@up2stay/up2promo-api-client';
import { Badge } from '@up2stay/up2promo-ui';
import { AlertTriangle, Clock } from 'lucide-react';

import { useEntitlementLabels } from '@/core/components/billing/upgrade-surfaces';
import { useDictionary } from '@/core/providers/dictionary-provider';

interface BillingStatusBadgeProps {
  plan: WorkspacePlan | undefined;
  status: SubscriptionStatus | undefined;
  workspace: Workspace | null;
}

function formatDate(value: string | null | undefined, locale: string): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

export function BillingStatusBadge({
  plan,
  status,
  workspace,
}: BillingStatusBadgeProps) {
  const dict = useDictionary();
  const statusDict = dict.workspace.billing.status;
  const { getPlanLabel } = useEntitlementLabels();
  const locale =
    typeof navigator !== 'undefined' ? navigator.language : 'de-DE';

  if (!plan) return null;

  if (status === SubscriptionStatus.TRIALING) {
    const trialEnd = formatDate(workspace?.currentPeriodEnd, locale);
    return (
      <Badge variant="secondary" className="gap-1">
        <Clock className="h-3 w-3" />
        {statusDict.trialing.replace('{date}', trialEnd)}
      </Badge>
    );
  }

  if (status === SubscriptionStatus.PAST_DUE) {
    return (
      <Badge variant="destructive" className="gap-1">
        <AlertTriangle className="h-3 w-3" />
        {statusDict.pastDue}
      </Badge>
    );
  }

  if (workspace?.cancelAtPeriodEnd) {
    const end = formatDate(workspace.currentPeriodEnd, locale);
    return (
      <Badge variant="outline">
        {statusDict.cancelling.replace('{date}', end)}
      </Badge>
    );
  }

  if (plan === WorkspacePlan.FREE) {
    return <Badge variant="outline">{getPlanLabel(plan)}</Badge>;
  }

  if (status === SubscriptionStatus.ACTIVE) {
    return <Badge variant="success">{statusDict.active}</Badge>;
  }

  return <Badge variant="secondary">{getPlanLabel(plan)}</Badge>;
}

export function BillingCycleLabel({ cycle }: { cycle?: BillingCycle }) {
  const dict = useDictionary();
  if (!cycle) return null;
  return (
    <span className="text-[13.5px] leading-relaxed text-muted-foreground">
      {cycle === BillingCycle.YEARLY
        ? dict.workspace.billing.cycleYearly
        : dict.workspace.billing.cycleMonthly}
    </span>
  );
}
